import { useRef, useCallback, useEffect } from 'react';
import { store } from '../store/store';
import { watchTimeApi } from '../services/watchTimeApi';

const FLUSH_INTERVAL_MS = 30_000;

/**
 * Отслеживает реальное время просмотра через state machine на основе событий Kodik.
 *
 * Реальный просмотр — только когда плеер играет (между play и pause/seek/end).
 * Скорость воспроизведения не влияет: считается wall-clock время перед экраном.
 *
 * Флаш использует RTK Query mutation через store.dispatch — автоматический
 * refresh токена при 401 через baseQueryWithReauth, без потери данных.
 *
 * Флаш происходит:
 *  1. Каждые 30 секунд (setInterval)
 *  2. При смене эпизода (onEpisodeChange) — перед сбросом контекста
 *  3. При уходе со страницы (useEffect cleanup — SPA-навигация)
 *  4. При закрытии вкладки (beforeunload) — fetch+keepalive как fallback
 *
 * @param {Object}  params
 * @param {string}  params.materialId  — UUID материала
 * @param {boolean} params.enabled     — активен только для авторизованных
 * @param {string}  params.accessToken — Bearer токен (для beforeunload fallback)
 */
export function useWatchTimeTracker({ materialId, enabled, accessToken }) {
  const episodeRef = useRef(0);
  const isPlayingRef = useRef(false);
  const playStartRef = useRef(null);
  const accumulatedRef = useRef(0);
  // Запоминаем состояние воспроизведения до ухода на фон
  const wasPlayingBeforeHiddenRef = useRef(false);

  // Стабильные refs — не пересоздают замыкания при изменении пропсов
  const materialIdRef = useRef(materialId);
  const enabledRef = useRef(enabled);
  const tokenRef = useRef(accessToken);

  useEffect(() => { materialIdRef.current = materialId; }, [materialId]);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);
  useEffect(() => { tokenRef.current = accessToken; }, [accessToken]);

  // --- Фиксирует текущий интервал воспроизведения в accumulated ---
  const commitCurrentInterval = useCallback(() => {
    if (!isPlayingRef.current || playStartRef.current === null) return;
    const elapsed = Math.floor((Date.now() - playStartRef.current) / 1000);
    if (elapsed > 0) accumulatedRef.current += elapsed;
    playStartRef.current = Date.now();
  }, []);

  // --- Флаш через RTK Query (с автоматическим refresh токена при 401) ---
  const flush = useCallback((episode, seconds, matId) => {
    if (!enabledRef.current || seconds < 1 || !matId) return;

    store.dispatch(
      watchTimeApi.endpoints.recordWatchTime.initiate({
        materialId: matId,
        episode,
        secondsWatched: Math.min(seconds, 3600),
      })
    );
  }, []);

  // --- Fallback флаш для beforeunload (fetch+keepalive, RTK Query не успевает) ---
  const beaconFlush = useCallback((episode, seconds, matId, token) => {
    if (!enabledRef.current || seconds < 1 || !matId || !token) return;

    fetch('https://api.doramahype.ru/api/v1/watch-time', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        material_id: matId,
        episode,
        seconds_watched: Math.min(seconds, 3600),
      }),
      keepalive: true,
    }).catch(() => {});
  }, []);

  // --- Периодический флаш каждые 30 секунд ---
  useEffect(() => {
    if (!enabled) return;

    const id = setInterval(() => {
      commitCurrentInterval();
      const seconds = accumulatedRef.current;
      if (seconds < 1) return;
      flush(episodeRef.current, seconds, materialIdRef.current);
      accumulatedRef.current = 0;
    }, FLUSH_INTERVAL_MS);

    return () => {
      clearInterval(id);
      // Флаш при SPA-навигации через React Router
      commitCurrentInterval();
      const seconds = accumulatedRef.current;
      if (seconds >= 1) {
        flush(episodeRef.current, seconds, materialIdRef.current);
        accumulatedRef.current = 0;
      }
    };
  }, [enabled, flush, commitCurrentInterval]);

  // --- Флаш при закрытии вкладки (beforeunload) ---
  useEffect(() => {
    if (!enabled) return;

    const handleUnload = () => {
      commitCurrentInterval();
      const seconds = accumulatedRef.current;
      if (seconds < 1) return;
      // RTK Query не успевает при unload — используем keepalive fetch
      beaconFlush(episodeRef.current, seconds, materialIdRef.current, tokenRef.current);
      accumulatedRef.current = 0;
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [enabled, beaconFlush, commitCurrentInterval]);

  // --- Пауза трекинга когда вкладка уходит в фон ---
  // Задержка 600ms игнорирует кратковременные скрытия при переходе в fullscreen
  // (macOS/Safari кратко помечают вкладку как hidden при входе/выходе из полного экрана)
  const visibilityTimerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        wasPlayingBeforeHiddenRef.current = isPlayingRef.current;
        // Ждём 600ms — если вкладка вернётся раньше (fullscreen переход), отменяем
        visibilityTimerRef.current = setTimeout(() => {
          commitCurrentInterval();
          isPlayingRef.current = false;
          playStartRef.current = null;
        }, 600);
      } else {
        // Вкладка вернулась — отменяем таймер если не успел сработать (fullscreen переход)
        if (visibilityTimerRef.current) {
          clearTimeout(visibilityTimerRef.current);
          visibilityTimerRef.current = null;
          // Таймер не сработал — значит это был fullscreen, продолжаем если играли
          if (wasPlayingBeforeHiddenRef.current) {
            playStartRef.current = Date.now();
          }
        }
        // Если таймер уже сработал — ждём kodik_player_play для возобновления
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityTimerRef.current) clearTimeout(visibilityTimerRef.current);
    };
  }, [enabled, commitCurrentInterval]);

  // --- Обработчики событий Kodik ---

  const onPlay = useCallback(() => {
    if (!enabledRef.current) return;
    isPlayingRef.current = true;
    playStartRef.current = Date.now();
  }, []);

  const onPause = useCallback(() => {
    if (!enabledRef.current) return;
    commitCurrentInterval();
    isPlayingRef.current = false;
    playStartRef.current = null;
  }, [commitCurrentInterval]);

  const onSeek = useCallback(() => {
    if (!enabledRef.current) return;
    if (isPlayingRef.current) commitCurrentInterval();
  }, [commitCurrentInterval]);

  const onVideoEnded = useCallback(() => {
    if (!enabledRef.current) return;
    commitCurrentInterval();
    isPlayingRef.current = false;
    playStartRef.current = null;
    const seconds = accumulatedRef.current;
    if (seconds >= 1) {
      flush(episodeRef.current, seconds, materialIdRef.current);
      accumulatedRef.current = 0;
    }
  }, [commitCurrentInterval, flush]);

  const onEpisodeChange = useCallback((episode) => {
    if (!enabledRef.current) return;
    commitCurrentInterval();
    const seconds = accumulatedRef.current;
    if (seconds >= 1) {
      flush(episodeRef.current, seconds, materialIdRef.current);
      accumulatedRef.current = 0;
    }
    episodeRef.current = episode;
    isPlayingRef.current = false;
    playStartRef.current = null;
  }, [commitCurrentInterval, flush]);

  return { onPlay, onPause, onSeek, onVideoEnded, onEpisodeChange };
}
